/**
 * `applyTheme()` — writes an {@link OrgTheme}'s CSS variables onto a DOM element
 * (ARCHITECTURE §7 `theme/`). Side-effecting counterpart to `themeToCssVars`.
 *
 * Defaults to `document.documentElement` (`:root`) so the vars cascade to the
 * whole app; the widget passes its isolated root instead.
 */

import type { OrgTheme } from '../types';
import { themeToCssVars } from './themeTokens';

/**
 * Apply theme variables to `target` (defaults to `:root`). Safe to call on every
 * theme change; only sets the `--lc-*` custom properties.
 *
 * @returns a function that removes the applied variables (for teardown/tests).
 */
export function applyTheme(
  theme: Partial<OrgTheme> | undefined,
  target?: HTMLElement,
): () => void {
  const el =
    target ??
    (typeof document !== 'undefined' ? document.documentElement : undefined);
  if (!el) {
    // Non-DOM environment (SSR/tests without jsdom) — no-op.
    return () => {};
  }
  const vars = themeToCssVars(theme);
  for (const [name, value] of Object.entries(vars)) {
    el.style.setProperty(name, value);
  }
  return () => {
    for (const name of Object.keys(vars)) {
      el.style.removeProperty(name);
    }
  };
}
